<template>
  <el-dialog
    :model-value="modelValue"
    title="链接消息"
    width="620px"
    append-to-body
    @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="font-medium text-slate-700 mb-3">物料配置</p>

    <!-- 按钮区：查看模式 / 编辑模式 -->
    <div class="flex mb-2">
      <template v-if="!isEditing">
        <el-button type="primary" size="small" @click="onAdd">添加链接</el-button>
        <el-button type="primary" size="small" plain @click="onBatchEdit">批量编辑</el-button>
        <el-button
          type="danger"
          size="small"
          :disabled="selectedRows.length === 0"
          @click="onDeleteSaved"
        >
          删除
        </el-button>
      </template>
      <template v-else>
        <el-button type="primary" size="small" plain @click="addRow">增加多条</el-button>
        <el-button
          type="danger"
          size="small"
          :disabled="selectedRows.length === 0"
          @click="onDeleteRow"
        >
          删除
        </el-button>
        <el-button type="primary" size="small" :loading="saveLoading" @click="onSave">
          保存
        </el-button>
      </template>
    </div>

    <DataTableWithPagination
      v-model:page="page"
      v-model:page-size="pageSize"
      :data="tableRows"
      :loading="loading"
      :total="total"
      :show-column-settings="false"
      size="small"
      highlight-current-row
      padding="0"
      @page-change="loadList"
      @size-change="loadList"
      @selection-change="(rows: WxLinkRow[]) => (selectedRows = rows)"
      @current-change="
        (row: WxLinkRow | null) => {
          if (!isEditing) selectedLink = row;
        }
      "
    >
      <el-table-column type="selection" width="44" />
      <el-table-column label="显示标题" width="180">
        <template #default="{ row }">
          <el-input v-if="isEditing" v-model="row.title" size="small" placeholder="显示标题" />
          <span v-else>{{ row.title }}</span>
        </template>
      </el-table-column>
      <el-table-column label="链接地址">
        <template #default="{ row }">
          <el-input v-if="isEditing" v-model="row.url" size="small" placeholder="链接地址" />
          <span v-else>{{ row.url }}</span>
        </template>
      </el-table-column>
    </DataTableWithPagination>

    <template #footer>
      <el-button @click="onCancel">取消</el-button>
      <el-button v-if="!isEditing" type="primary" :disabled="!confirmTarget" @click="onConfirm">
        确认
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import type { WxLinkRow } from '@/types/chatBox';

const props = defineProps<{
  modelValue: boolean;
  accountId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'confirm', link: Record<string, unknown>): void;
}>();

const loading = ref(false);
const saveLoading = ref(false);
const isEditing = ref(false);
const tableRows = ref<WxLinkRow[]>([]);
const selectedRows = ref<WxLinkRow[]>([]);
const selectedLink = ref<WxLinkRow | null>(null);

/**
 * 待发送的链接：优先取点击行，其次取勾选。
 * 勾选框本用于批量编辑/删除，但用户看到勾选框就会用它选发送对象，
 * 只认点击行会让「明明勾上了却不能确认」；勾多行时对象不唯一，仍视为未选。
 */
const confirmTarget = computed<WxLinkRow | null>(
  () =>
    selectedLink.value ??
    (selectedRows.value.length === 1 ? (selectedRows.value[0] ?? null) : null),
);
const total = ref(0);
const page = ref(1);
const pageSize = ref(10);

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      isEditing.value = false;
      selectedLink.value = null;
      selectedRows.value = [];
      page.value = 1;
      loadList();
    }
  },
);

async function loadList() {
  loading.value = true;
  try {
    const { apiPost } = await import('@/utils/request');
    const data = await apiPost<{ data?: unknown[]; total?: number }>(
      '/channel-materials/wx-link-list',
      { account_id: props.accountId || '', page: page.value, page_size: pageSize.value },
    ).catch(() => ({ data: [], total: 0 }));
    const list = ((data as { data?: unknown[] })?.data as Record<string, unknown>[]) || [];
    tableRows.value = list.map((r) => ({
      id: r.id as number | undefined,
      title: (r.title as string) || '',
      url: (r.url as string) || '',
    }));
    total.value = (data as { total?: number })?.total || 0;
  } finally {
    loading.value = false;
  }
}

function emptyRow(): WxLinkRow {
  return { title: '', url: '', isNew: true };
}

function onAdd() {
  isEditing.value = true;
  selectedRows.value = [];
  tableRows.value.push(emptyRow());
}

function onBatchEdit() {
  isEditing.value = true;
  selectedRows.value = [];
}

function addRow() {
  tableRows.value.push(emptyRow());
}

function onDeleteRow() {
  tableRows.value = tableRows.value.filter((r) => !selectedRows.value.includes(r));
  selectedRows.value = [];
}

async function onDeleteSaved() {
  if (selectedRows.value.length === 0) return;
  await ElMessageBox.confirm('确定删除选中的链接吗？', '提示', { type: 'warning' });
  const ids = selectedRows.value.map((r) => r.id).filter(Boolean);
  const { apiPost } = await import('@/utils/request');
  await apiPost('/channel-materials/wx-link-delete', {
    account_id: props.accountId || '',
    ids,
  });
  ElMessage.success('删除成功');
  loadList();
}

async function onSave() {
  const rows = tableRows.value.filter((r) => r.title.trim() || r.url.trim());
  if (rows.length === 0) {
    ElMessage.warning('请至少填写一条数据');
    return;
  }
  saveLoading.value = true;
  try {
    const { apiPost } = await import('@/utils/request');
    await apiPost('/channel-materials/wx-link-save', {
      account_id: props.accountId || '',
      items: rows.map(({ id, title, url }) => ({ id, title, url })),
    });
    ElMessage.success('保存成功');
    isEditing.value = false;
    selectedRows.value = [];
    loadList();
  } finally {
    saveLoading.value = false;
  }
}

function onCancel() {
  if (isEditing.value) {
    isEditing.value = false;
    loadList();
  } else {
    emit('update:modelValue', false);
  }
}

function onConfirm() {
  if (!confirmTarget.value) return;
  emit('confirm', confirmTarget.value as unknown as Record<string, unknown>);
  emit('update:modelValue', false);
}
</script>
